<template>
  <div class="camera-instructions">
    <ion-grid class="ion-no-padding">

      <ion-row class="instructions-row">
        <ion-col id="camera-instruction-text" size="9"
                 v-if="settings['proctor_camera_instructions'] !== null && settings['proctor_camera_instructions'] !== ''">
          <html-content :content="settings['proctor_camera_instructions']"></html-content>
        </ion-col>

        <ion-col>
          <ion-img v-if="settings['proctor_camera_photo_id_required']" class="capture-photo-id"
                   :src="BASE_URL + '/static/images/monitoring-capture-photo-id.svg'"/>
          <ion-img v-else class="capture-photo"
                   :src="BASE_URL + '/static/images/monitoring-capture-photo.svg'"/>
        </ion-col>
      </ion-row>

      <ion-row>
        <ion-col>
          <Banner
              :display-icon="true"
              :message="banner_message"
              v-if="displayBanner"
              banner-type="warning"
              style="margin-bottom: 15px;"/>
        </ion-col>
      </ion-row>

      <ion-row class="drop-shadow-row">
        <ion-col size="12">
          <ion-row id="intro-video" class="camera-container">
            <div id="image-wrapper" v-if="capturedImage">
              <img :src="capturedImage"/>
            </div>

            <div id="detection-error-wrapper"
                 v-if="camera_loading==false &&  detected_face_message !== null && detected_face_message!== ''">
              {{ detected_face_message }}
            </div>

            <div class="camera-controls">

              <ion-button
                  v-if="!capturedImage && cameraConnected"
                  color="light"
                  :disabled="faceDetected === false"
                  @click="captureImage">
                <ion-icon
                    class="icon"
                    slot="icon-only"
                    aria-hidden="true"
                    size="medium"
                    :src="require('@/assets/icons/camera-capture.svg')"/>
              </ion-button>
              <ion-button
                  v-if="capturedImage && cameraConnected"
                  color="light"
                  @click="onRetake">
                <ion-icon
                    class="icon"
                    slot="icon-only"
                    aria-hidden="true"
                    size="medium"
                    :src="require('@/assets/icons/camera-retake.svg')"/>
              </ion-button>
            </div>

          </ion-row>

        </ion-col>
        <ion-col size="12">
          <CameraDevice v-if="showCameraControls"/>

        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</template>

<script setup>
import {computed, getCurrentInstance, onMounted, reactive, ref, watch} from 'vue';
import {IonButton, IonCol, IonGrid, IonIcon, IonImg, IonRow} from '@ionic/vue';
import {useStore} from 'vuex';
import HtmlContent from '@/components/html/HtmlContent.vue';
import Banner from '@/components/html/Banner.vue';
import CameraDevice from "@/components/test/CameraDevice.vue";


const emit = defineEmits([
  'complete',
  'cancel',
]);

const BASE_URL = process.env.BASE_URL;

const store = useStore();

const settings = reactive(store.state.Test.settings['test']);
const language = reactive(store.getters.languageSet);
const capturedImage = ref(null)
const displayBanner = ref(false);
const banner_message = ref(null);
const permission_denied = ref(false);
const detected_face_message = ref(null);
const camera_loading = ref(true);
// loading the image capture and submit library
const instance = getCurrentInstance();

const globalEmitter = instance.appContext.config.globalProperties.$globalEmitter;

onMounted(()=>{

  // set some delay to ensure all the components are mounted
  setTimeout(()=>{
    globalEmitter.emit('start-camera');
  }, 300)

})



const faceDetected = computed(() => {
  const numFaceDetected = store.getters['Monitoring/numberOfFaceDetected'];
  if (numFaceDetected === 0) {
    detected_face_message.value = language.no_face_detected;
    return false;
  }

  if (settings.proctor_camera_photo_id_required) { // with ID
    if (numFaceDetected === 1 || numFaceDetected === 2) {
      detected_face_message.value = null;
      return true;
    }
  } else {  //without ID
    if (numFaceDetected === 1) {
      detected_face_message.value = null;
      return true;
    }
  }
  detected_face_message.value = language.multiple_face_detected;
  return false;
})

const cameraCounts = computed(() => {
  return store.getters["Monitoring/cameraDeviceList"].length
})

const showCameraControls = computed(() => {

  let result = cameraCounts.value > 0 && permission_denied.value == false && cameraConnected.value == true;
  return result
})

const onRetake = () => {
  capturedImage.value = null;
  emit('complete', false);
  toggleCameraFeed()
};

const captureImage = () => {
  // add flash animation for taking a photo
  let introVideoSection = document.querySelector('#intro-video');
  introVideoSection.classList.add('flash-active');

  // remove flash
  setTimeout(() => {
    introVideoSection.classList.remove('flash-active');
  }, 300); // Match this timeout to the CSS animation duration

  // take the screen shot and enable the test start button
  /**
   * Safari cannot handle the multiple CSS changes with aysnchronous manner.
   * So we had to separate two actions (remove flash and take photos)
   */

  setTimeout(() => {
    globalEmitter.emit('capture-intro-image');
  }, 310);
}

const cameraConnected = computed(() => {
  return store.getters['Monitoring/isCameraConnected'];
})


const toggleCameraFeed = (() => {
  if (capturedImage.value !== null) {
    document.querySelector("#cmVideoLiveView").style.display = "none";
  } else {
    document.querySelector("#cmVideoLiveView").style.display = "block";
  }
})

globalEmitter.on('camera-permission-denied', () => {
  permission_denied.value = true;
  banner_message.value = language['camera_no_permission']
  __handlePermissionOrCameraDisconnected()
})

globalEmitter.on('no-device-permission', () => {
  banner_message.value = language['test_monitoring_camera_disconnect_message']
  __handlePermissionOrCameraDisconnected()
})

function __handlePermissionOrCameraDisconnected() {
  capturedImage.value = false;
  let cmLiveView = document.querySelector('#cmVideoLiveView');
  cmLiveView.style.display = 'block';
  displayBanner.value = true
}


globalEmitter.on('intro-image-captured', (image) => {
  capturedImage.value = image;
  toggleCameraFeed()
  emit('complete');
})


watch(cameraConnected, (newValue, oldValue) => {

      if (newValue === true) {
        displayBanner.value = false;
        setTimeout(() => {
          camera_loading.value = false;
        }, 500)


      } else if (oldValue === true && newValue === false) {
        displayBanner.value = true;
        banner_message.value = language['test_monitoring_camera_disconnect_message']
        __handlePermissionOrCameraDisconnected()
        emit('complete', false);
      }
    }
);

const cameraPermission = computed(() => {
  return store.state.Monitoring.cameraPermissionStatus
})

watch(cameraPermission, (newValue, oldValue) => {
  if (newValue === 'denied' || newValue === 'prompt') {
    displayBanner.value = true;
    permission_denied.value = true;
    banner_message.value = language['camera_no_permission']
    emit('complete', false);
  }
})

watch(cameraCounts, (newValue, oldValue) => {
  if (newValue === 0) {

    displayBanner.value = true;
    banner_message.value = language['test_monitoring_camera_disconnect_message']
  }
})


defineExpose({
  capturedImage,
});
</script>

<style lang="scss" scoped>
//@import '@/theme/variables';

.camera-instructions {

  .instructions-row {
    padding-top: 15px;
    padding-bottom: 15px;

    #camera-instruction-text {
      :deep(.bbcode ul) {
        margin: 0px;
        padding: 0px;
        list-style-position: inside;
      }
    }
  }

  .drop-shadow-row {
    display: flex;
    max-width: 668px;
    padding: 40px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    border-radius: 10px;
    border: 1px solid #E9ECEF;
    background: #FFFFFF;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.04);
  }

  #intro-video {
    margin-left: auto;
    margin-right: auto;
    width: inherit;
    aspect-ratio: 1/1;
    border-radius: 8px;
    display: flex;
    justify-content: center;


    .camera-controls {
      position: absolute;
      z-index: 1000;
      bottom: 28px;

      ion-button {
        width: 64px;
        height: 64px;
        flex-shrink: 0;
        --border-radius: 100px;

      }
    }

    #image-wrapper {
      width: 100%;
      height: 100%;
      border-radius: 8px;
      display: flex;
      background-color: black;
      justify-content: center;
      align-items: center;
    }

    img {
      width: 100%;
      height: 100%;
      border-radius: 8px;
    }
  }
}


.display-none {
  display: none;
}

.display-block {
  display: block;
}

/* Keyframes for the flash animation */
@keyframes flash-animation {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1; /* Quick flash to full white */
  }
  100% {
    opacity: 0; /* Fade back to transparent */
  }
}

/* Class to apply the animation */
.flash-active {
  animation: flash-animation 0.4s ease-out forwards; /* 0.4s duration, ease-out for quick start, forwards to stay at end state */
}

#detection-error-wrapper {
  display: inline-flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #FFE073;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  bottom: 99px;
  z-index: 999;
}

</style>